import * as _ from 'lodash';
import { compareCalendarDate } from '../../misc/utility';
import { AttendanceModel } from './AttendanceModel';

/**
 * @typedef { import('../../objects/Attendance').AttendanceList } AttendanceList
 * @typedef { import('../../objects/Attendance').Attendance } Attendance
 * @typedef { import('../../objects/Mission').Mission } Mission
 * @typedef { import('../../objects/MissionGroup').MissionGroup } MissionGroup
 */

export class AttendanceCollection {
    /**
     * @param {AttendanceList} attendanceList
     * @param {Mission[]} missions
     * @param {MissionGroup[]} missionGroups
     * @param {boolean} isPolitikerEnabled
     */
    constructor(attendanceList, missions, missionGroups, isPolitikerEnabled) {
        this.meeting = attendanceList.Meeting;
        this.attendances = _.map(attendanceList.Attendances, x => AttendanceModel.createExisting(x, isPolitikerEnabled));
        this.missions = missions;
        this.missionGroups = _.sortBy(missionGroups, x => x.Name);
        this.isPolitikerEnabled = isPolitikerEnabled;
        this.missionsById = null;
        this.removed = [];

        const grouped = _.groupBy(missions, x => x.PersonId);
        const groups = _.map(grouped, x => ({ PersonId: x[0].PersonId, Person: x[0].Person, Missions: x }));
        this.groups = _.orderBy(groups, [x => x.Person.LastName, x => x.Person.firstName]);

        this._updateAvailable();
        this._updateOrder();
    }

    get isChanged() {
        return this.removed.length > 0 || _.some(this.attendances, x => x.isAdded || x.isChanged);
    }

    _updateOrder() {
        this.attendances = AttendanceModel.order(this.attendances);
        this.attendances.forEach((item, index) => void (item.$$order = index));
    }

    _updateAvailable() {
        var persons = _.map(this.attendances, x => x.getPersonId());
        this.available = _.filter(this.groups, x => !_.includes(persons, x.PersonId));
    }

    /**
     * @param {Mission} mission
     */
    _addMission(mission) {
        const att = AttendanceModel.createNew(this.meeting, mission, this.isPolitikerEnabled);
        if (compareCalendarDate(this.meeting.Date, new Date()) < 0) {
            att.presentTime = this.meeting.MeetingTime;
        }
        this.attendances.push(att);
    }

    /**
     * @param {AttendanceModel} existing
     * @param {AttendanceModel} replacement
     */
    replaceAttendance(existing, replacement) {
        if (replacement.attendance.isLegacy()) throw "Invalid replacement, missing mission";
        if (existing.attendance.isLegacy()) throw "Invalid existing, missing mission";
        if (existing.attendance.MissionId !== replacement.attendance.MissionId) throw "Incompatible mission";

        let pos = this.attendances.indexOf(existing);
        if (pos >= 0) {
            this.attendances[pos] = replacement;
            this._updateAvailable();
            this._updateOrder();
        }
    }

    /**
     * @param {AttendanceModel} attendanceModel
     */
    getAvailableMissions(attendanceModel) {
        var attendancePersonId = attendanceModel.getPersonId();
        var group = _.find(this.groups, x => x.PersonId === attendancePersonId);
        return (group == null) ? null : group.Missions;
    }

    /**
     * @param {AttendanceModel} attendanceModel
     * @param {Mission} mission
     */
    specifyMission(attendanceModel, mission) {
        attendanceModel.specifyMission(mission);
        this._updateAvailable();
        this._updateOrder();
    }

    /**
     * @param {boolean} withSubstitutes
     */
    includeMembers(withSubstitutes) {
        let changed = false;

        for (let mission of this.missions) {
            if (mission.MandatePeriod.OrganizationId !== this.meeting.OrganizationId)
                continue;
            if (!withSubstitutes && mission.MissionType.IsSubstitute)
                continue;

            if (this.include(mission)) {
                changed = true;
            }
        }

        if (changed) {
            this._updateOrder();
        }

        return changed;
    }

    /**
     * @param {Mission} mission
     */
    include(mission) {

        var index = _.findIndex(this.available, x => x.PersonId === mission.PersonId);

        if (index < 0)
            return false;

        this.available.splice(index, 1);
        this._addMission(mission);
        this._updateOrder();

        return true;
    }

    /**
     * @param {AttendanceModel} am
     */
    _removeAttendanceModel(am) {
        var index = this.attendances.indexOf(am);
        if (index >= 0) {
            this.attendances.splice(index, 1);

            if (am.attendance.Id) {
                this.removed.push(am.attendance.Id);
            }
        }
    }

    /**
     * @param { AttendanceModel[] } attendanceModels
     */
    exclude(attendanceModels) {
        for (let am of attendanceModels) {
            this._removeAttendanceModel(am);
        }
        this._updateAvailable();
    }

    /**
     * @param {string[]} missionIds
     */
    includeMissionIds(missionIds) {
        if (this.missionsById == null) {
            this.missionsById = _.mapKeys(this.missions, x => x.Id);
        }

        let changed = false;

        for (let addId of missionIds) {
            const mission = this.missionsById[addId];
            if (!mission) continue;

            var index = _.findIndex(this.available, x => x.PersonId === mission.PersonId);
            if (index < 0) continue;

            changed = true;
            this.available.splice(index, 1);
            this._addMission(mission);
        }

        if (changed) {
            this._updateOrder();
        }

        return changed;
    }

    createUpdate() {
        return {
            Added: this.attendances.filter(x => x.isAdded).map(x => x.createAdd()),
            Updated: this.attendances.filter(x => x.isChanged).map(x => x.createUpdate()),
            Removed: this.removed,
        };
    }
}